import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiError, track } from "../lib/api";
import { gaEvent } from "../lib/analytics";
import { ScoreGauge } from "../components/ScoreGauge";
import { CategoryBars } from "../components/CategoryBars";
import { useAuth } from "../context/AuthContext";
import { usePublicConfig } from "../lib/useConfig";
import { detectMarket, setMarket, currencyForMarket, formatPrice, formatAmount, fullEffectivePrice, fullRegularPrice, launchActive } from "../lib/currency";
import { MarketSelector } from "../components/MarketSelector";
import { toast } from "sonner";
import { Lock, TrendingUp, AlertTriangle, ArrowRight, Loader2, Check, Sparkles, Tag } from "lucide-react";

export default function FreeResults() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const config = usePublicConfig();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [coupon, setCoupon] = useState("");
  const [showCoupon, setShowCoupon] = useState(false);
  const [market, setMarketState] = useState(detectMarket());
  const currency = currencyForMarket(market);
  const handleMarket = (code) => { setMarketState(code); setMarket(code); };
  const [busyTier, setBusyTier] = useState(null);

  useEffect(() => {
    api.get(`/assessments/${id}/results`).then((r) => setData(r.data))
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || "Not found"));
  }, [id]);

  const upgrade = async (tier) => {
    track("upgrade_click", id);
    setBusyTier(tier);
    try {
      if (user) await api.post(`/assessments/${id}/claim`).catch(() => {});
      const { data: co } = await api.post("/payments/checkout", {
        assessment_id: id, origin_url: window.location.origin, tier, currency,
        coupon_code: coupon || undefined, email: user?.email,
      });
      try { localStorage.setItem("fl_last_session", co.session_id); } catch (e) { /* ignore */ }
      gaEvent("payment_initiated", { assessment_id: id, tier, currency, provider: co.provider });
      window.location.href = co.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusyTier(null);
    }
  };

  if (error) return <div className="max-w-2xl mx-auto px-5 py-24 text-center text-muted-foreground">{error}</div>;
  if (!data) return <div className="py-24 text-center"><Loader2 className="animate-spin mx-auto text-gold" /></div>;

  if (data.paid && data.purchased_tier !== "focus") { navigate(`/report/${id}`); return null; }

  const { scores, readiness_category, insights, org_name } = data;
  const alreadyFocus = data.paid && data.purchased_tier === "focus";
  const upgradeCurrency = data.paid_currency || currency;
  const paidAmt = Number(data.paid_amount || 0);
  const fullEff = fullEffectivePrice(config?.pricing, upgradeCurrency);
  const upgradeAmount = fullEff != null ? Math.max(0, fullEff - paidAmt) : null;
  const onLaunch = launchActive(config?.pricing, currency);

  return (
    <div>
      <section className="bg-green text-cream relative grain overflow-hidden">
        <div className="relative z-10 max-w-4xl mx-auto px-5 sm:px-8 py-16 text-center">
          <span className="text-ochre text-xs font-semibold tracking-[0.2em] uppercase">Your snapshot</span>
          <h1 className="mt-3 text-3xl sm:text-4xl font-bold">{org_name || "Your Organization"}</h1>
          <div className="mt-8 flex justify-center">
            <div className="bg-offwhite rounded-md p-8 inline-block">
              <ScoreGauge score={scores.overall_score} label={readiness_category} size={230} />
            </div>
          </div>
          <p className="mt-6 text-cream/75 text-sm leading-relaxed max-w-2xl mx-auto">
            This reflects how ready your organization looks to pursue funding, based on what you shared. It's not a
            measure of how much your mission matters, and it doesn't predict whether any funder will say yes.
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-16">
        <div className="grid sm:grid-cols-3 gap-5">
          <div className="p-6 bg-white border border-line rounded-md" data-testid="insight-strength">
            <TrendingUp className="text-forest mb-3" size={22} />
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">What's working</p>
            <p className="font-display font-semibold text-ink">{insights.strength || "—"}</p>
          </div>
          <div className="p-6 bg-white border border-line rounded-md" data-testid="insight-gap">
            <AlertTriangle className="text-clay mb-3" size={22} />
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Worth your attention</p>
            <p className="font-display font-semibold text-ink">{insights.gap || "—"}</p>
          </div>
          <div className="p-6 bg-white border border-line rounded-md" data-testid="insight-nextstep">
            <Sparkles className="text-ochre mb-3" size={22} />
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">One thing to do next</p>
            <p className="text-sm text-ink/80 leading-snug">{insights.next_step || "—"}</p>
          </div>
        </div>

        {/* Tier choice — surfaced right after the snapshot (moment of highest interest) */}
        <div className="mt-14 relative">
          <div className="p-8 bg-green text-cream rounded-md relative grain overflow-hidden">
            <div className="relative z-10 max-w-2xl mx-auto text-center">
              <p className="text-xl font-serif-accent text-ochre">{alreadyFocus ? "You've got the Focus report." : "Your score tells you where you stand."}</p>
              <p className="text-2xl font-bold mt-1">{alreadyFocus ? "Ready for the full plan?" : "Here's what to do next."}</p>

              {!alreadyFocus && (
              <div className="mt-6">
                <MarketSelector market={market} onChange={handleMarket} dark reassure={false} />
              </div>
              )}
              {alreadyFocus && (
                <p className="mt-4 text-cream/70 text-sm" data-testid="upgrade-credit-note">You've already paid {formatAmount(upgradeCurrency, paidAmt)} — you only pay the difference to upgrade.</p>
              )}

              <div className={`mt-6 grid gap-4 text-left ${alreadyFocus ? "max-w-sm mx-auto" : "sm:grid-cols-2"}`}>
                {!alreadyFocus && (
                <div className="p-5 bg-cream/5 border border-cream/15 rounded-md flex flex-col">
                  <h3 className="font-display font-bold">Focus Report</h3>
                  <div className="mt-1 font-display text-2xl font-extrabold">{formatPrice(config?.pricing, "focus", currency)}</div>
                  <ul className="mt-3 space-y-1.5 text-sm text-cream/85 flex-1">
                    {["All 5 category scores", "Your top strengths", "What needs your attention", "Recommended funding paths"].map((x) => (
                      <li key={x} className="flex gap-2"><Check size={14} className="text-ochre mt-0.5 shrink-0" />{x}</li>
                    ))}
                  </ul>
                  <button onClick={() => upgrade("focus")} disabled={busyTier} data-testid="buy-focus"
                    className="mt-4 w-full border border-ochre text-cream font-semibold py-2.5 rounded-[3px] hover:bg-ochre hover:text-ink transition disabled:opacity-60">
                    {busyTier === "focus" ? <Loader2 className="animate-spin mx-auto" size={16} /> : `Get My Focus Report — ${formatPrice(config?.pricing, "focus", currency)}`}
                  </button>
                </div>
                )}
                <div className="p-5 bg-cream/10 border border-ochre/50 rounded-md flex flex-col relative">
                  <span className="absolute -top-2.5 right-4 text-[10px] bg-ochre/90 text-ink font-semibold px-2 py-0.5 rounded-full">Full Strategy</span>
                  <h3 className="font-display font-bold">Full Fundability Blueprint</h3>
                  <div className="mt-1 font-display text-2xl font-extrabold">{alreadyFocus ? formatAmount(upgradeCurrency, upgradeAmount) : formatPrice(config?.pricing, "full", currency)}</div>
                  {alreadyFocus ? (
                    <p className="text-[11px] text-ochre font-semibold uppercase tracking-wider mt-0.5">Upgrade price</p>
                  ) : (onLaunch && (
                    <p className="text-[11px] mt-0.5"><span className="text-ochre font-semibold uppercase tracking-wider">Launch price</span><span className="text-cream/50"> · Regularly {formatAmount(currency, fullRegularPrice(config?.pricing, currency))}</span></p>
                  ))}
                  <ul className="mt-3 space-y-1.5 text-sm text-cream/85 flex-1">
                    {["Everything in Focus", "Detailed analysis & funding story", "Approaches to prioritize (and pause)", "30/60/90-day action plan", "Downloadable Blueprint"].map((x) => (
                      <li key={x} className="flex gap-2"><Check size={14} className="text-ochre mt-0.5 shrink-0" />{x}</li>
                    ))}
                  </ul>
                  <button onClick={() => upgrade("full")} disabled={busyTier} data-testid="unlock-blueprint"
                    className="mt-4 btn-gold w-full font-semibold py-2.5 rounded-[3px] disabled:opacity-60">
                    {busyTier === "full" ? <Loader2 className="animate-spin mx-auto" size={16} /> : (alreadyFocus ? `Get My Blueprint — ${formatAmount(upgradeCurrency, upgradeAmount)}` : `Get My Full Blueprint — ${formatPrice(config?.pricing, "full", currency)}`)}
                  </button>
                </div>
              </div>

              <p className="mt-4 text-cream/60 text-xs">{currency === "usd" ? "Coupons apply to card payments. " : ""}Secure payment options available at checkout.</p>

              {currency === "usd" && (showCoupon ? (
                <div className="mt-4 flex gap-2 max-w-xs mx-auto">
                  <input value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder="Coupon code"
                    className="flex-1 px-3 py-2.5 rounded-[3px] bg-cream/10 border border-cream/20 text-cream placeholder:text-cream/40 outline-none focus:border-ochre" data-testid="coupon-input" />
                </div>
              ) : (
                <button onClick={() => setShowCoupon(true)} className="mt-3 text-cream/60 text-sm flex items-center gap-1.5 mx-auto hover:text-ochre">
                  <Tag size={14} /> Have a coupon?
                </button>
              ))}
              {!user && <p className="mt-4 text-cream/50 text-xs">Tip: <Link to={`/register?redirect=/results/${id}`} className="text-ochre underline">create an account</Link> to keep your report in one place.</p>}
            </div>
          </div>

          {/* Supporting peek at the locked full report */}
          <div className="mt-8 rounded-md border border-line overflow-hidden bg-white">
            <div className="p-8">
              <h2 className="text-2xl font-bold text-ink font-display">A peek at what's inside</h2>
              <p className="mt-2 text-ink/70">Your full category breakdown and written analysis unlock after you upgrade.</p>
            </div>
            <div className="relative">
              <div className="p-8 pt-0 grid sm:grid-cols-2 gap-8 blur-[6px] select-none pointer-events-none" aria-hidden="true">
                <CategoryBars scores={scores.category_scores} />
                <div className="space-y-2">
                  <div className="h-3 bg-line rounded w-3/4" /><div className="h-3 bg-line rounded" />
                  <div className="h-3 bg-line rounded w-5/6" /><div className="h-3 bg-line rounded w-2/3" />
                  <div className="h-3 bg-line rounded w-4/5" /><div className="h-3 bg-line rounded w-1/2" />
                </div>
              </div>
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-ivory/40 backdrop-blur-[2px]">
                <div className="w-12 h-12 rounded-full bg-navy flex items-center justify-center">
                  <Lock className="text-gold" size={20} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
